import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { MdAdd, MdDelete, MdEdit, MdLocationOn } from "react-icons/md";
import { Pagination } from "@mui/material";
import { AuthContext } from "../../../contexts/AuthContext";
import { tempUrl, useStateContext } from "../../../contexts/ContextProvider";
import { ConfirmDeleteDialog, InfoDialog, Loader, ScrollToTop, SearchBar } from "../../../components";
import { ShowTableDataVerifikasi } from "../../../components/ShowTable";

const TampilDataVerifikasi = () => {
  const { user, dispatch } = useContext(AuthContext);
  const { screenSize } = useStateContext();
  const navigate = useNavigate();
  const location = useLocation();
  const id = location.pathname.split("/")[2];

  const [isDataVerifikasiExist, setIsDataVerifikasiExist] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [fotoKunjungan, setFotoKunjungan] = useState([]);
  const [alasan, setAlasan] = useState("");
  const [isLockedByValidasiDataVerifikator, setIsLockedByValidasiDataVerifikator] = useState(false);
  const [dataVerifikasisData, setDataVerifikasisData] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  let [page, setPage] = useState(0);
  const [limit] = useState(20);
  const [pages, setPages] = useState(0);
  const [query, setQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    id ? getDataVerifikasiById() : setIsDataVerifikasiExist(false);
  }, [id]);

  useEffect(() => {
    getDataVerifikasis();
  }, [page, searchTerm]);

  const getPhotoUrl = (photo) => (typeof photo === "string" ? photo : photo?.url);

  const getPhotoLocation = (photo) => {
    if (typeof photo === "string" || photo?.latitude == null || photo?.longitude == null) {
      return null;
    }

    return {
      latitude: photo.latitude,
      longitude: photo.longitude,
      accuracy: photo.accuracy,
      capturedAt: photo.capturedAt,
    };
  };

  const formatCapturedAt = (capturedAt) => {
    if (!capturedAt) {
      return "-";
    }

    return new Intl.DateTimeFormat("id-ID", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(new Date(capturedAt));
  };

  const getGoogleMapsUrl = (photoLocation) => {
    return `https://www.google.com/maps?q=${photoLocation.latitude},${photoLocation.longitude}`;
  };

  const getMapEmbedUrl = (photoLocation) => {
    const latitude = Number(photoLocation.latitude);
    const longitude = Number(photoLocation.longitude);
    const delta = 0.004;
    return `https://www.openstreetmap.org/export/embed.html?bbox=${longitude - delta}%2C${latitude - delta}%2C${longitude + delta}%2C${latitude + delta}&layer=mapnik&marker=${latitude}%2C${longitude}`;
  };

  const lastLocatedPhoto = fotoKunjungan
    .map((photo) => ({
      photo,
      location: getPhotoLocation(photo),
    }))
    .filter((item) => item.location)
    .sort((a, b) => new Date(a.location.capturedAt || 0) - new Date(b.location.capturedAt || 0))
    .pop();

  const handleAuthError = (err) => {
    if (err.response?.status === 401) {
      dispatch({ type: "LOGOUT" });
      navigate("/");
    }
  };

  const handleChange = (e, p) => {
    setPage(p - 1);
  };

  const searchData = (e) => {
    e.preventDefault();
    setPage(0);
    setSearchTerm(query);
  };

  const getDataVerifikasis = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${tempUrl}/dataVerifikasisPagination?search_query=${encodeURIComponent(searchTerm)}&page=${page}&limit=${limit}`,
        {
          id: user._id,
          token: user.token,
        },
      );
      setQuery(searchTerm);
      setDataVerifikasisData(response.data.dataVerifikasis);
      setPage(response.data.page);
      setPages(response.data.totalPage);
    } catch (err) {
      handleAuthError(err);
      setError(err.response?.data?.message || "Gagal mengambil data Data Verifikasi.");
    }
    setLoading(false);
  };

  const getDataVerifikasiById = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${tempUrl}/dataVerifikasis/${id}`, {
        id: user._id,
        token: user.token,
      });
      setIsDataVerifikasiExist(true);
      setAdmin(response.data.admin);
      setFotoKunjungan(response.data.fotoKunjungan || []);
      setAlasan(response.data.alasan || "");
      setIsLockedByValidasiDataVerifikator(Boolean(response.data.isLockedByValidasiDataVerifikator));
    } catch (err) {
      handleAuthError(err);
      setError(err.response?.data?.message || "Gagal mengambil detail Data Verifikasi.");
    }
    setLoading(false);
  };

  const deleteDataVerifikasi = async () => {
    setShowDeleteConfirm(false);
    setLoading(true);
    try {
      await axios.post(`${tempUrl}/deleteDataVerifikasi/${id}`, {
        id: user._id,
        token: user.token,
      });
      navigate("/dataVerifikasi");
      await getDataVerifikasis();
    } catch (err) {
      handleAuthError(err);
      setDeleteError(err.response?.data?.message || "Gagal menghapus Data Verifikasi.");
      setLoading(false);
    }
  };

  const renderMapLastLocation = () => {
    if (!lastLocatedPhoto) {
      return null;
    }

    return (
      <section className="section-panel">
        <div className="toolbar mt-0">
          <div>
            <h2 className="h6 fw-bold mb-1">Lokasi Foto Terakhir</h2>
            <div className="text-muted">
              Foto Kunjungan
              {lastLocatedPhoto.location.accuracy ? ` - Akurasi ${Math.round(lastLocatedPhoto.location.accuracy)} meter` : ""}
            </div>
          </div>
          <Button
            className="btn-icon"
            size="sm"
            variant="outline-primary"
            onClick={() => window.open(getGoogleMapsUrl(lastLocatedPhoto.location), "_blank", "noopener,noreferrer")}
          >
            <MdLocationOn /> Google Maps
          </Button>
        </div>
        <div className="map-preview mt-3">
          <iframe
            title="Lokasi Foto Terakhir"
            src={getMapEmbedUrl(lastLocatedPhoto.location)}
            loading="lazy"
          />
        </div>
      </section>
    );
  };

  const renderFotoKunjungan = () => {
    return (
      <section className="section-panel mt-3">
        <h2 className="h6 fw-bold mb-3">Foto Kunjungan</h2>
        {fotoKunjungan.length === 0 && <div className="text-muted">Belum ada foto.</div>}
        <div className="photo-grid">
          {fotoKunjungan.map((img, i) => {
            const photoUrl = getPhotoUrl(img);
            const photoLocation = getPhotoLocation(img);

            return (
              <div className="photo-card" key={`foto-kunjungan-${i}`}>
                <button type="button" onClick={() => window.open(photoUrl, "_blank", "noopener,noreferrer")}>
                  <img src={photoUrl} alt={`Foto Kunjungan ${i + 1}`} />
                  <span>No. {i + 1}</span>
                </button>
                <div className="photo-meta">
                  <div>
                    <strong>Waktu</strong>
                    <span>{formatCapturedAt(img?.capturedAt)}</span>
                  </div>
                </div>
                {photoLocation && (
                  <Button
                    className="photo-map-btn"
                    size="sm"
                    variant="outline-primary"
                    type="button"
                    onClick={() => window.open(getGoogleMapsUrl(photoLocation), "_blank", "noopener,noreferrer")}
                  >
                    <MdLocationOn /> Lihat Maps
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div>
      <ScrollToTop />
      <div className="page-header">
        <div>
          <div className="page-kicker">Survey</div>
          <h1 className="page-title">Data Verifikasi</h1>
        </div>
        <Button className="btn-icon" size="sm" onClick={() => navigate("/dataVerifikasi/tambahDataVerifikasi")}>
          <MdAdd /> Tambah
        </Button>
      </div>
      {error && <Alert variant="warning">{error}</Alert>}

      {isDataVerifikasiExist && (
        <>
          <section className="section-panel">
            <div className="toolbar mt-0">
              <div className="detail-title-actions">
                <Button className="btn-back" size="sm" variant="outline-secondary" onClick={() => navigate("/dataVerifikasi")}>
                  {"< Kembali"}
                </Button>
                <div>
                  <h2 className="h6 fw-bold mb-1">Detail Data Verifikasi</h2>
                  <div className="text-muted">{admin?.namaPemohon?.permohonan?.namaPemohon}</div>
                </div>
              </div>
              {!isLockedByValidasiDataVerifikator && (
                <div className="d-flex gap-2 flex-wrap">
                  <Button className="btn-icon" size="sm" variant="outline-primary" onClick={() => navigate(`/dataVerifikasi/${id}/edit`)}>
                    <MdEdit /> Ubah
                  </Button>
                  <Button className="btn-icon" size="sm" variant="outline-danger" onClick={() => setShowDeleteConfirm(true)}>
                    <MdDelete /> Hapus
                  </Button>
                </div>
              )}
            </div>
            {isLockedByValidasiDataVerifikator && (
              <Alert variant="info" className="mt-3 mb-0">
                Data Verifikasi sudah berstatus VALID di Validasi Data Verifikator, sehingga tidak bisa diubah atau dihapus.
              </Alert>
            )}
            <hr />
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <div className="detail-label">Nama Pemohon</div>
                  <div className="detail-value">{admin?.namaPemohon?.permohonan?.namaPemohon || "-"}</div>
                </div>
                <div className="mb-3">
                  <div className="detail-label">No Jual</div>
                  <div className="detail-value">{admin?.noJual || "-"}</div>
                </div>
                <div className="mb-3">
                  <div className="detail-label">Nopol</div>
                  <div className="detail-value">{admin?.nopol || "-"}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <div className="detail-label">Alamat</div>
                  <div className="detail-value">{admin?.alamat || "-"}</div>
                </div>
                <div className="mb-3">
                  <div className="detail-label">Alasan</div>
                  <div className="detail-value">{alasan || "-"}</div>
                </div>
              </Col>
            </Row>
          </section>
          {renderMapLastLocation()}
          {renderFotoKunjungan()}
        </>
      )}

      <Form onSubmit={searchData}>
        <div className="search-row">
          <SearchBar value={query} setSearchTerm={setQuery} />
          <Button type="submit">Cari</Button>
        </div>
      </Form>
      <ShowTableDataVerifikasi currentPosts={dataVerifikasisData} />
      <div className="search-row">
        <Pagination
          count={pages}
          page={page + 1}
          onChange={handleChange}
          color="primary"
          size={screenSize <= 600 ? "small" : "large"}
        />
      </div>
      <ConfirmDeleteDialog
        show={showDeleteConfirm}
        message={`Yakin ingin menghapus Data Verifikasi ${admin?.namaPemohon?.permohonan?.namaPemohon}?`}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={deleteDataVerifikasi}
      />
      <InfoDialog
        show={Boolean(deleteError)}
        title="Data Tidak Bisa Dihapus"
        message={deleteError}
        onClose={() => setDeleteError("")}
      />
    </div>
  );
};

export default TampilDataVerifikasi;
